import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { GlassCard } from '../../components/GlassCard/GlassCard'
import { ResponsiveImage } from '../../components/ResponsiveImage/ResponsiveImage'
import { ProtectedImage } from '../../components/ProtectedImage/ProtectedImage'
import { SectionLabel } from '../../components/SectionLabel/SectionLabel'
import { SectionScaffold } from '../../components/SectionScaffold/SectionScaffold'
import { assets } from '../../data/assets'
import { content } from '../../data/content'
import {
  createMotionMedia,
  gsap,
  initializeWhenNear,
  motionConditions,
  motionTokens,
  type MotionConditionState,
} from '../../motion'
import styles from './SpaceCommerceSection.module.css'

const galleryItems = [
  {
    src: '/assets/images/微信图片_20260814160316_79_2.jpg',
    caption: '设计稿',
  },
  {
    src: '/assets/images/图片生成-1786620485737.png',
    caption: '效果图',
  },
  {
    src: '/assets/images/图片生成-1786620254944.png',
    caption: '现场实景融合',
  },
] as const

const simulationVideo = '/assets/videos/延时 (2).mp4'
const simulationVideoMobile = '/assets/optimized/videos/hero-mobile-1080.mp4'
const simulationPoster = '/assets/optimized/posters/hero-1920.webp'

export function SpaceCommerceSection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const closingVisualRef = useRef<HTMLElement>(null)
  const galleryRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    scrollLeft: 0,
  })
  const [galleryProgress, setGalleryProgress] = useState(1 / galleryItems.length)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [isVideoLightboxOpen, setIsVideoLightboxOpen] = useState(false)
  const copy = content['SEC-002']
  const diagram = assets['AST-004']
  const environment = assets['AST-005']

  useEffect(() => {
    if (lightboxIndex === null && !isVideoLightboxOpen) return

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setLightboxIndex(null)
        setIsVideoLightboxOpen(false)
      }
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isVideoLightboxOpen, lightboxIndex])

  const updateGalleryProgress = () => {
    const gallery = galleryRef.current
    if (!gallery) return

    const scrollableWidth = gallery.scrollWidth - gallery.clientWidth
    const scrollProgress =
      scrollableWidth > 0 ? gallery.scrollLeft / scrollableWidth : 0
    setGalleryProgress(
      (1 + scrollProgress * (galleryItems.length - 1)) / galleryItems.length,
    )
  }

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return

    const gallery = galleryRef.current
    if (!gallery) return

    dragStateRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      scrollLeft: gallery.scrollLeft,
    }
    gallery.setPointerCapture(event.pointerId)
    gallery.classList.add(styles.isDragging)
  }

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gallery = galleryRef.current
    if (!gallery || !dragStateRef.current.active) return

    event.preventDefault()
    if (Math.abs(event.clientX - dragStateRef.current.startX) > 6) {
      dragStateRef.current.moved = true
    }
    gallery.scrollLeft =
      dragStateRef.current.scrollLeft -
      (event.clientX - dragStateRef.current.startX)
  }

  const stopDragging = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gallery = galleryRef.current
    if (!gallery || !dragStateRef.current.active) return

    dragStateRef.current.active = false
    gallery.classList.remove(styles.isDragging)
    if (gallery.hasPointerCapture(event.pointerId)) {
      gallery.releasePointerCapture(event.pointerId)
    }
  }

  useLayoutEffect(() => {
    const section = sectionRef.current
    const closingVisual = closingVisualRef.current
    if (!section || !closingVisual) return

    return initializeWhenNear(
      section,
      () => {
        const media = createMotionMedia()

        media.add(motionConditions, (context) => {
          const { desktop, reduceMotion } =
            context.conditions as MotionConditionState
          const revealStart = desktop ? 'top 85%' : 'top 92%'
          const reveals = Array.from(
            section.querySelectorAll<HTMLElement>('[data-space-reveal]'),
          )
          const counters = Array.from(
            section.querySelectorAll<HTMLElement>('[data-counter]'),
          )

          if (reduceMotion) {
            gsap.set(reveals, { clearProps: 'all', autoAlpha: 1, y: 0 })
            counters.forEach((counter) => {
              counter.textContent = counter.dataset.target ?? counter.textContent
            })
            gsap.set(closingVisual, { clearProps: 'all', scale: 1 })
            return
          }

          reveals.forEach((element) => {
            gsap.fromTo(
              element,
              { autoAlpha: 0, y: motionTokens.distance.medium },
              {
                autoAlpha: 1,
                y: 0,
                duration: motionTokens.duration.normal,
                ease: motionTokens.ease.entrance,
                scrollTrigger: {
                  trigger: element,
                  start: revealStart,
                  once: true,
                },
              },
            )
          })

          counters.forEach((counter) => {
            const target = Number(counter.dataset.target)
            const value = { current: 0 }
            counter.textContent = '0'

            gsap.to(value, {
              current: target,
              duration: motionTokens.duration.normal,
              ease: motionTokens.ease.standard,
              snap: { current: 1 },
              onUpdate: () => {
                counter.textContent = String(Math.round(value.current))
              },
              scrollTrigger: {
                trigger: counter,
                start: desktop ? 'top 80%' : 'top 90%',
                once: true,
              },
            })
          })

          gsap.fromTo(
            closingVisual,
            { scale: motionTokens.scale.cinematic },
            {
              scale: 1,
              duration: motionTokens.duration.slow,
              ease: motionTokens.ease.entrance,
              scrollTrigger: {
                trigger: closingVisual,
                start: revealStart,
                once: true,
              },
            },
          )
        })

        return () => media.revert()
      },
      '0px 0px -10% 0px',
    )
  }, [])

  const heading = (
    <header className={styles.heading}>
      <SectionLabel>— 02 / SPACE &amp; COMMERCE</SectionLabel>
      <h2 id="sec-002-title" className={styles.title}>
        {copy.title}
      </h2>
      <p className={styles.subtitle}>{copy.subtitle}</p>
    </header>
  )

  return (
    <SectionScaffold
      id="SEC-002"
      className={styles.section}
      labelledBy="sec-002-title"
    >
      <div ref={sectionRef} className={styles.sectionInner}>
        <div className={styles.compactHeading}>{heading}</div>

        <div className={styles.split}>
          <div className={styles.visualColumn}>
            <figure className={styles.diagramFrame}>
              <ResponsiveImage
                className={styles.diagram}
                src={diagram.path}
                alt={diagram.alt}
                width={diagram.width}
                height={diagram.height}
                sizes="(max-width: 47.9375rem) 100vw, (max-width: 63.9375rem) calc(100vw - 4rem), 44vw"
              />
            </figure>
          </div>

          <div className={styles.narrative}>
            <div className={styles.desktopHeading}>{heading}</div>

            <div data-space-reveal className={styles.contentBlock}>
              <p className={styles.body}>{copy.body}</p>
            </div>

            <GlassCard data-space-reveal className={styles.metricCard}>
              <div className={styles.metric} aria-hidden="true">
                <span data-counter data-target="14">
                  14
                </span>
                <span className={styles.metricUnit}>m</span>
              </div>
              <p>{copy.data.installation}</p>
            </GlassCard>

            <GlassCard data-space-reveal className={styles.metricCard}>
              <div className={styles.metric} aria-hidden="true">
                <span data-counter data-target="25">
                  25
                </span>
                <span className={styles.metricUnit}>个</span>
              </div>
              <p>{copy.data.market}</p>
            </GlassCard>

            <div data-space-reveal className={styles.dimensionBlock}>
              <p>{copy.data.booth}</p>
            </div>
          </div>
        </div>

        <figure className={styles.environmentFrame}>
          <ResponsiveImage
            ref={closingVisualRef}
            className={styles.environment}
            src={environment.path}
            alt={environment.alt}
            width={environment.width}
            height={environment.height}
            loading="lazy"
            sizes="100vw"
          />
          <span className={styles.environmentShade} aria-hidden="true" />
        </figure>

        <section
          data-space-reveal
          className={styles.galleryModule}
          aria-label="项目视觉推演画廊"
        >
          <div
            ref={galleryRef}
            className={styles.galleryTrack}
            onScroll={updateGalleryProgress}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={stopDragging}
            onPointerCancel={stopDragging}
          >
            {galleryItems.map((item, index) => (
              <figure className={styles.galleryItem} key={item.src}>
                <button
                  className={styles.galleryImageButton}
                  type="button"
                  onClick={(event) => {
                    if (dragStateRef.current.moved) {
                      event.preventDefault()
                      dragStateRef.current.moved = false
                      return
                    }
                    setLightboxIndex(index)
                  }}
                  aria-label={`全屏查看${item.caption}`}
                >
                  <ResponsiveImage
                    className={styles.galleryImage}
                    src={item.src}
                    alt={item.caption}
                    loading="lazy"
                    draggable="false"
                    sizes="(max-width: 47.9375rem) 82vw, (max-width: 63.9375rem) 76vw, 76vw"
                  />
                </button>
                <figcaption className={styles.caption}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <span className={styles.captionText}>
                    <span>{item.caption}</span>
                    <span className={styles.captionHint}>点击看大图</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className={styles.progressTrack} aria-hidden="true">
            <span
              className={styles.progressValue}
              style={{ transform: `scaleX(${galleryProgress})` }}
            />
          </div>
        </section>

        <figure data-space-reveal className={styles.videoModule}>
          <div className={styles.videoFrame}>
            <ResponsiveImage
              className={styles.video}
              src={galleryItems[2].src}
              alt=""
              sizes="100vw"
              loading="lazy"
              aria-hidden="true"
            />
            <span className={styles.videoShade} aria-hidden="true" />
            <button
              className={styles.videoControl}
              type="button"
              onClick={() => setIsVideoLightboxOpen(true)}
              aria-label="全屏观看模拟效果视频"
            >
              <span className={styles.videoPlayBadge} aria-hidden="true">
                <span className={styles.playIcon} />
              </span>
            </button>
          </div>
          <figcaption className={styles.caption}>
            <span>04</span>
            <span className={styles.captionText}>
              <span>模拟效果</span>
              <span className={styles.captionHint}>点击全屏观看</span>
            </span>
          </figcaption>
        </figure>
      </div>

      {lightboxIndex !== null ? (
        <div
          className={styles.lightbox}
          role="dialog"
          aria-modal="true"
          aria-label={`${galleryItems[lightboxIndex].caption}大图`}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) setLightboxIndex(null)
          }}
        >
          <button
            className={styles.lightboxClose}
            type="button"
            onClick={() => setLightboxIndex(null)}
            aria-label="关闭大图"
          >
            <span aria-hidden="true" />
          </button>
          <figure className={styles.lightboxFigure}>
            <ProtectedImage
              className={styles.lightboxImage}
              src={galleryItems[lightboxIndex].src}
              alt={galleryItems[lightboxIndex].caption}
              width={lightboxIndex === 0 ? 3172 : 3840}
              height={lightboxIndex === 0 ? 2304 : 2160}
              draggable={false}
            />
            <figcaption className={styles.lightboxCaption}>
              {galleryItems[lightboxIndex].caption}
            </figcaption>
          </figure>
        </div>
      ) : null}

      {isVideoLightboxOpen ? (
        <div
          className={styles.lightbox}
          role="dialog"
          aria-modal="true"
          aria-label="模拟效果全屏视频"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsVideoLightboxOpen(false)
            }
          }}
        >
          <button
            className={styles.lightboxClose}
            type="button"
            onClick={() => setIsVideoLightboxOpen(false)}
            aria-label="关闭全屏视频"
          >
            <span aria-hidden="true" />
          </button>
          <figure className={styles.videoLightboxFigure}>
            <video
              className={styles.videoLightboxPlayer}
              poster={simulationPoster}
              autoPlay
              controls
              playsInline
              preload="auto"
              draggable={false}
            >
              <source
                media="(max-width: 47.9375rem)"
                src={simulationVideoMobile}
                type="video/mp4"
              />
              <source src={simulationVideo} type="video/mp4" />
            </video>
            <figcaption className={styles.lightboxCaption}>
              模拟效果
            </figcaption>
          </figure>
        </div>
      ) : null}
    </SectionScaffold>
  )
}
